import { useRef, useState, type ReactNode } from 'react'

import type { ProductSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Notice,
  useScopedKeydown,
} from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'
import { money } from '../lib/inbox-commerce.ts'
import { createScanCapture, isEditableTarget } from '../lib/scan-capture.ts'
import { currentScanOwner } from '../lib/scan-owner.ts'
import { resolveScan } from '../lib/scan-resolve.ts'

import { SalesDocumentDialog } from './sales-document-dialog.tsx'

/**
 * O bipe em qualquer tela — F206.
 *
 * Montado uma vez no shell. Um bipe com o foco FORA de um campo:
 *
 * - numa tela que sabe bipar (PDV, Etiquetas) vai para ela, como se tivesse
 *   caído no campo dela;
 * - nas outras, abre o que o código é: o cupom abre a venda; a etiqueta abre a
 *   ficha rápida do produto.
 *
 * Com o foco num campo, nada aqui age: o bipe é do campo.
 */
export const GlobalScan = (): ReactNode => {
  const { allows, session } = useAuth()
  const capture = useRef(createScanCapture())
  const [saleId, setSaleId] = useState<string | null>(null)
  const [product, setProduct] = useState<{
    product: ProductSummary
    variantId: string
  } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const branchId = session?.context?.branchId ?? undefined

  const handle = (code: string): void => {
    const owner = currentScanOwner()
    if (owner !== null) {
      owner(code)
      return
    }
    setNotice(null)
    resolveScan(code, {
      branchId,
      canReadSales: allows('sales.read'),
      canReadCatalog: allows('catalog.read'),
    })
      .then((result) => {
        if (result.kind === 'sale') {
          setProduct(null)
          setSaleId(result.documentId)
        } else if (result.kind === 'product') {
          setSaleId(null)
          setProduct({ product: result.product, variantId: result.variantId })
        } else {
          setNotice(`Código ${result.code} não encontrado.`)
        }
      })
      .catch(() => {
        setNotice('Não foi possível procurar o código agora.')
      })
  }

  useScopedKeydown((event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (isEditableTarget(event.target)) {
      capture.current.reset()
      return
    }
    const code = capture.current.key(event.key, event.timeStamp)
    if (code === null) return
    // O Enter do leitor não pode "clicar" no botão que por acaso tem o foco.
    event.preventDefault()
    handle(code)
  })

  return (
    <>
      {saleId === null ? null : (
        <SalesDocumentDialog
          documentId={saleId}
          onClose={() => {
            setSaleId(null)
          }}
        />
      )}
      {product === null ? null : (
        <ProductQuickView
          product={product.product}
          variantId={product.variantId}
          onClose={() => {
            setProduct(null)
          }}
        />
      )}
      {notice === null ? null : (
        // No canto dos avisos (F248), e não por cima do pedido da loja.
        <Notice>
          <div
            role="status"
            className="flex items-center gap-3 rounded-(--radius-md) border border-(--border-default) bg-(--surface-raised) px-4 py-3 text-(length:--text-sm) shadow-(--shadow-md)"
          >
            {notice}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNotice(null)
              }}
            >
              Fechar
            </Button>
          </div>
        </Notice>
      )}
    </>
  )
}

const ProductQuickView = ({
  product,
  variantId,
  onClose,
}: {
  product: ProductSummary
  variantId: string
  onClose: () => void
}): ReactNode => {
  const variant = product.variants.find((entry) => entry.id === variantId)
  const available = product.stock?.available
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={product.name}
        description={[variant?.name, variant?.sku, variant?.gtin]
          .filter(
            (part): part is string =>
              part !== null && part !== undefined && part !== ''
          )
          .join(' · ')}
      >
        <dl className="grid grid-cols-2 gap-3 text-(length:--text-sm)">
          <div>
            <dt className="text-(--text-muted)">Preço</dt>
            <dd className="font-(--font-mono) text-(length:--text-lg) font-(--weight-semibold) tabular-nums">
              {money(product.price?.basePrice)}
            </dd>
          </div>
          <div>
            <dt className="text-(--text-muted)">Estoque nesta filial</dt>
            <dd>
              {!product.tracksStock ? (
                <Badge tone="neutral">Não controla</Badge>
              ) : available === undefined ? (
                '—'
              ) : (
                <Badge tone={Number(available) > 0 ? 'success' : 'danger'}>
                  {available}
                </Badge>
              )}
            </dd>
          </div>
        </dl>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
